import { Country } from '@/lib/enums/Country'
import { isOrderFieldWithinLimit, ORDER_FIELD_MAX_LENGTHS } from '@/lib/order-field-limits'
import { subscriptionPreferredWeekdayOptions } from '@/lib/subscription-frequency'
import { z } from 'zod/v4'

type OrderField = keyof typeof ORDER_FIELD_MAX_LENGTHS

/** `refine()` arguments that hold a field to its persisted length, counted in code points. */
function maxOrderFieldCharacters(
  field: OrderField,
  label: string
): [check: (value: string) => boolean, params: { message: string }] {
  return [
    (value) => isOrderFieldWithinLimit(field, value),
    {
      message: `${label} cannot be over ${ORDER_FIELD_MAX_LENGTHS[field]} characters long.`,
    },
  ]
}

const deliveryAddressSchema = z.object({
  name: z
    .string()
    .refine(...maxOrderFieldCharacters('address_name', 'Business, funeral home, or church'))
    .optional(),
  line1: z
    .string()
    .min(1, 'Address line 1 is required')
    .refine(...maxOrderFieldCharacters('address_line1', 'Address line 1')),
  line2: z
    .string()
    .refine(...maxOrderFieldCharacters('address_line2', 'Address line 2'))
    .optional(),
  city: z
    .string()
    .min(1, 'City is required')
    .refine(...maxOrderFieldCharacters('address_city', 'City')),
  state: z.string().min(1, 'State is required'),
  postal_code_suffix: z.string().nullish(),
  postal_code: z
    .string()
    .min(1, 'Postal code is required')
    .length(5, 'Please enter a valid 5-digit zip code'),
  country: z.string().default(Country.UnitedStates.Value).optional(),
})

const recipientNameSchema = z
  .string()
  .trim()
  .min(1, 'Recipient name is required')
  .refine(...maxOrderFieldCharacters('recipient_name', 'Recipient name'))

const optionalRecipientPhoneSchema = z
  .string()
  .refine(...maxOrderFieldCharacters('recipient_phone', 'Phone'))
  .optional()

const optionalRecipientEmailSchema = z
  .email('Please enter a valid email')
  .refine(...maxOrderFieldCharacters('recipient_email', 'Email'))
  .or(z.literal(''))
  .optional()

const optionalSpecialInstructionsSchema = z
  .string()
  .refine(...maxOrderFieldCharacters('special_instructions', 'Special instructions'))
  .optional()

const optionalPreferredWeekdaySchema = z
  .enum(subscriptionPreferredWeekdayOptions.map((option) => option.id))
  .or(z.literal(''))
  .optional()

export const deliveryFormSchema = z.object({
  deliveryAddress: deliveryAddressSchema,
  deliveryDate: z.string().min(1, 'Delivery date is required'),
  preferredWeekday: optionalPreferredWeekdaySchema,
  recipientName: recipientNameSchema,
  recipientPhone: optionalRecipientPhoneSchema,
  recipientEmail: optionalRecipientEmailSchema,
  specialInstructions: optionalSpecialInstructionsSchema,
})

export const pickupFormSchema = z.object({
  pickupDate: z.string().min(1, 'Pickup date is required'),
  preferredWeekday: optionalPreferredWeekdaySchema,
  recipientName: recipientNameSchema,
  recipientPhone: optionalRecipientPhoneSchema,
  recipientEmail: optionalRecipientEmailSchema,
  specialInstructions: optionalSpecialInstructionsSchema,
})
